import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { formatCents, formatDate } from '@/lib/utils';
import { ESTIMATE_STATUS_BADGE, PO_STATUS_BADGE } from '@/lib/badges';
import { isAreaUnit } from '@billflow/shared';
import type { InventoryItem, StockAdjustment } from '@billflow/shared';
import { ItemEditModal } from '@/components/ItemEditModal';
import { SortableTh } from '@/components/SortableTh';
import { useClientSort, type SortValue } from '@/hooks/useClientSort';
import {
  buildSalesRows,
  buildPurchaseRows,
  computeSalesFooter,
  computePurchaseFooter,
  type LedgerEstimateItem,
  type LedgerSalesReturn,
  type LedgerPoItem,
  type LedgerPurchaseReturn,
  type LedgerReconciliation,
  type SalesLedgerRow,
  type PurchaseLedgerRow,
} from '@/lib/itemLedger';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ItemResponse {
  data: { item: InventoryItem & { low_stock: boolean } };
}

interface LedgerResponse {
  data: {
    item: InventoryItem & { low_stock: boolean };
    stock_adjustments: StockAdjustment[];
    estimate_items: LedgerEstimateItem[];
    po_items: LedgerPoItem[];
    sales_returns: LedgerSalesReturn[];
    purchase_returns: LedgerPurchaseReturn[];
    reconciliation: LedgerReconciliation;
    current_stock: number;
  };
}

// ─── Module-level constants + display helpers ────────────────────────────────────

type Tab = 'adjustments' | 'sales' | 'purchases';

const tabs: { label: string; value: Tab }[] = [
  { label: 'Sales History', value: 'sales' },
  { label: 'Purchase History', value: 'purchases' },
  { label: 'Stock Adjustments', value: 'adjustments' },
];

// ─── Sort accessors (stable module consts — one per ledger table) ─────────────────
// Rows are discriminated unions: `status` exists only on sale/purchase rows, not on
// return rows → return null there so those rows sort last on a status sort.

const salesAccessors: Record<string, (r: SalesLedgerRow) => SortValue> = {
  number: (r) => r.number,
  date: (r) => r.date,
  status: (r) => ('status' in r ? r.status : null),
  qty: (r) => r.qty,
  unit_price: (r) => r.unit_price,
  line_total: (r) => r.line_total,
};

const purchaseAccessors: Record<string, (r: PurchaseLedgerRow) => SortValue> = {
  number: (r) => r.number,
  date: (r) => r.date,
  status: (r) => ('status' in r ? r.status : null),
  qty: (r) => r.qty,
  unit_price: (r) => r.unit_price,
};

const adjustmentAccessors: Record<string, (r: StockAdjustment) => SortValue> = {
  created_at: (r) => r.created_at,
  direction: (r) => r.direction,
  qty: (r) => r.qty,
  reason: (r) => r.reason,
};

// Quantities are stored 2dp (sq ft) so summing them can leave float noise — round for display.
const roundQty = (n: number): number => Math.round(n * 100) / 100;

// A signed quantity for the reconciliation column: "+40", "-30", or "0".
function fmtSignedQty(n: number): string {
  const r = roundQty(n);
  return r > 0 ? `+${r}` : String(r);
}

// ─── Info field helper ─────────────────────────────────────────────────────────

function InfoField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-foreground">
        {value ?? <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

// ─── Reconciliation line ─────────────────────────────────────────────────────────

// `value` is the movement's effect on stock (already signed), so the column sums to
// Current Stock. Negatives render red.
function ReconLine({ label, value }: { label: string; value: number }) {
  const r = roundQty(value);
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`tabular-nums ${r < 0 ? 'text-red-600' : 'text-foreground'}`}>
        {fmtSignedQty(r)}
      </dd>
    </div>
  );
}

// ─── Reason formatter ──────────────────────────────────────────────────────────

function formatReason(reason: string): string {
  return reason.charAt(0).toUpperCase() + reason.slice(1);
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function ItemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('sales');

  const {
    data: itemData,
    isLoading: itemLoading,
    error: itemError,
  } = useQuery({
    queryKey: ['item', id],
    queryFn: () => apiFetch<ItemResponse>(`/api/inventory/${id}`),
    enabled: !!id,
  });

  const { data: ledgerData, isLoading: ledgerLoading } = useQuery({
    queryKey: ['item-ledger', id],
    queryFn: () => apiFetch<LedgerResponse>(`/api/inventory/${id}/ledger`),
    enabled: !!id,
  });

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ['item', id] });
    queryClient.invalidateQueries({ queryKey: ['item-ledger', id] });
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
  }

  // Ledger-derived rows — computed here (with `?? []` fallbacks so they're safe before
  // the item guard below) and fed to the sort hooks. The sort hooks MUST stay
  // top-level/unconditional, so they live above the early returns, not in the tabs.
  const ledger = ledgerData?.data;
  const salesRows = buildSalesRows(ledger?.estimate_items ?? [], ledger?.sales_returns ?? []);
  const purchaseRows = buildPurchaseRows(ledger?.po_items ?? [], ledger?.purchase_returns ?? []);

  // Defaults mirror today's order (buildSalesRows/buildPurchaseRows return newest-first;
  // stock adjustments were listed newest-first) so nothing shifts until a header is clicked.
  const salesSort = useClientSort(salesRows, {
    defaultKey: 'date',
    defaultDir: 'desc',
    accessors: salesAccessors,
  });
  const purchaseSort = useClientSort(purchaseRows, {
    defaultKey: 'date',
    defaultDir: 'desc',
    accessors: purchaseAccessors,
  });
  const adjustmentSort = useClientSort(ledger?.stock_adjustments ?? [], {
    defaultKey: 'created_at',
    defaultDir: 'desc',
    accessors: adjustmentAccessors,
  });

  if (itemLoading) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (itemError || !itemData?.data.item) {
    return (
      <div className="p-6">
        <Link
          to="/items"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Items
        </Link>
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {itemError instanceof Error ? itemError.message : 'Item not found.'}
        </div>
      </div>
    );
  }

  const item = itemData.data.item;
  const isGoods = item.category === 'goods';

  // Info-card margin (cents) + percent of selling price (only when selling > 0).
  const margin = item.selling_price - item.purchase_price;
  const marginPct =
    item.selling_price > 0 ? ` (${((margin / item.selling_price) * 100).toFixed(1)}%)` : '';

  // Reconciliation — trust the contract, but fall back gracefully if the ledger hasn't
  // loaded (or an older server predates the field) so the card never white-screens.
  const recon: LedgerReconciliation = ledger?.reconciliation ?? {
    opening_other: 0,
    purchased: 0,
    sold: 0,
    sales_returns: 0,
    purchase_returns: 0,
    adjustments: 0,
    current: ledger?.current_stock ?? 0,
  };

  // Footer totals stay computed from the FULL data (not the sorted view) — sorting a
  // column must never change net sold / net purchased.
  const salesFooter = computeSalesFooter(ledger?.estimate_items ?? [], ledger?.sales_returns ?? []);
  const purchaseFooter = computePurchaseFooter(
    ledger?.po_items ?? [],
    ledger?.purchase_returns ?? [],
  );

  return (
    <div className="p-6 max-w-6xl">
      {/* Back nav */}
      <Link
        to="/items"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Items
      </Link>

      {/* Top row: item info (left) + stock details (right, goods only) */}
      <div className={`grid grid-cols-1 gap-4 mb-6 ${isGoods && ledger ? 'lg:grid-cols-3' : ''}`}>
        {/* Info Card */}
        <Card className={isGoods && ledger ? 'lg:col-span-2' : ''}>
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <CardTitle className="text-xl">{item.name}</CardTitle>
                {item.category === 'goods' ? (
                  <Badge variant="blue">Goods</Badge>
                ) : (
                  <Badge variant="gray">Service</Badge>
                )}
                {item.status === 'active' ? (
                  <Badge variant="green">Active</Badge>
                ) : (
                  <Badge variant="red">Inactive</Badge>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditOpen(true)}
                className="shrink-0"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6 items-start">
              {/* Item photo — shown only when image_path is set */}
              {item.image_path && (
                <div className="shrink-0">
                  <img
                    src={`/api/inventory/${item.id}/image?v=${encodeURIComponent(item.image_path)}`}
                    alt={item.name}
                    className="w-28 h-28 rounded-lg object-cover border border-border shadow-sm bg-muted"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4">
                  <InfoField label="Code" value={item.code} />
                  <InfoField
                    label="Category"
                    value={item.category === 'goods' ? 'Goods' : 'Service'}
                  />
                  <InfoField label="Unit" value={item.unit} />
                  <InfoField label="Selling Price" value={formatCents(item.selling_price)} />
                  <InfoField label="Purchase Price" value={formatCents(item.purchase_price)} />
                  <InfoField
                    label="Margin"
                    value={
                      <span className={margin < 0 ? 'text-red-600' : undefined}>
                        {formatCents(margin)}
                        {marginPct}
                      </span>
                    }
                  />
                  <InfoField
                    label="Preferred Supplier"
                    value={
                      item.preferred_supplier_id != null ? (
                        <Link
                          to={`/suppliers/${item.preferred_supplier_id}`}
                          className="text-primary hover:underline"
                        >
                          {item.preferred_supplier_name ??
                            `Supplier #${item.preferred_supplier_id}`}
                        </Link>
                      ) : null
                    }
                  />
                  {isGoods && (
                    <>
                      {item.sqft_per_box != null && (
                        <InfoField label="Sq Ft / Box" value={item.sqft_per_box} />
                      )}
                      {item.sqft_per_piece != null && (
                        <InfoField label="Sq Ft / Piece" value={item.sqft_per_piece} />
                      )}
                      {!isAreaUnit(item.unit) && item.pack_qty != null && (
                        <InfoField
                          label="Pack Contents"
                          value={`${item.pack_qty}${item.pack_unit ? ` ${item.pack_unit}` : ''}`}
                        />
                      )}
                    </>
                  )}
                  {item.weight != null && <InfoField label="Weight" value={`${item.weight} lb`} />}
                  {item.dimensions != null && item.dimensions !== '' && (
                    <InfoField label="Dimensions" value={item.dimensions} />
                  )}
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stock details card — goods only */}
        {isGoods && ledger && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Stock details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                  Current Stock
                </div>
                <div className="text-3xl font-semibold tabular-nums">{roundQty(recon.current)}</div>
              </div>

              {/* Reconciliation breakdown — signed movements that sum to Current Stock */}
              <dl className="space-y-1.5 text-sm border-t pt-3">
                <ReconLine label="Opening / other" value={recon.opening_other} />
                <ReconLine label="Purchased" value={recon.purchased} />
                <ReconLine label="Sold" value={-recon.sold} />
                <ReconLine label="Sales returns" value={recon.sales_returns} />
                <ReconLine label="Purchase returns" value={-recon.purchase_returns} />
                <ReconLine label="Adjustments" value={recon.adjustments} />
              </dl>

              {/* Min stock + stock value */}
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t pt-3">
                <InfoField label="Min Stock" value={item.min_stock} />
                <InfoField
                  label="Stock Value"
                  value={
                    <span>
                      {formatCents(item.stock_qty * item.purchase_price)}{' '}
                      <span className="text-xs text-muted-foreground">at cost</span>
                    </span>
                  }
                />
              </dl>

              {item.low_stock && (
                <div className="flex items-center gap-1.5 text-sm text-amber-700">
                  <Badge variant="amber">Low</Badge>
                  <span>Below minimum of {item.min_stock}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Ledger Tabs */}
      <div>
        {/* Tab bar */}
        <div className="flex border-b mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.value
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Stock Adjustments */}
        {activeTab === 'adjustments' && (
          <div className="rounded-md border overflow-hidden">
            {!isGoods ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                Services do not track stock.
              </div>
            ) : ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !ledger?.stock_adjustments?.length ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                No stock adjustments yet.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <SortableTh
                      label="Date"
                      sortKey="created_at"
                      sort={adjustmentSort.sort}
                      dir={adjustmentSort.dir}
                      onToggle={adjustmentSort.toggleSort}
                    />
                    <SortableTh
                      label="Direction"
                      sortKey="direction"
                      sort={adjustmentSort.sort}
                      dir={adjustmentSort.dir}
                      onToggle={adjustmentSort.toggleSort}
                    />
                    <SortableTh
                      label="Qty"
                      sortKey="qty"
                      sort={adjustmentSort.sort}
                      dir={adjustmentSort.dir}
                      onToggle={adjustmentSort.toggleSort}
                      align="right"
                      className="w-20"
                    />
                    <SortableTh
                      label="Reason"
                      sortKey="reason"
                      sort={adjustmentSort.sort}
                      dir={adjustmentSort.dir}
                      onToggle={adjustmentSort.toggleSort}
                      className="w-32"
                    />
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {adjustmentSort.sorted.map((adj) => (
                    <tr key={`adj-${adj.id}`} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-muted-foreground">
                        {formatDate(adj.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        {adj.direction === 'increase' ? (
                          <Badge variant="green">Increase</Badge>
                        ) : (
                          <Badge variant="red">Decrease</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{adj.qty}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {formatReason(adj.reason)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{adj.notes ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Tab: Sales History (estimates + sales returns) */}
        {activeTab === 'sales' && (
          <div className="rounded-md border overflow-hidden">
            {ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !salesRows.length ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                No sales history yet.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <SortableTh
                      label="Estimate"
                      sortKey="number"
                      sort={salesSort.sort}
                      dir={salesSort.dir}
                      onToggle={salesSort.toggleSort}
                    />
                    <SortableTh
                      label="Date"
                      sortKey="date"
                      sort={salesSort.sort}
                      dir={salesSort.dir}
                      onToggle={salesSort.toggleSort}
                    />
                    <SortableTh
                      label="Status"
                      sortKey="status"
                      sort={salesSort.sort}
                      dir={salesSort.dir}
                      onToggle={salesSort.toggleSort}
                    />
                    <SortableTh
                      label="Qty"
                      sortKey="qty"
                      sort={salesSort.sort}
                      dir={salesSort.dir}
                      onToggle={salesSort.toggleSort}
                      align="right"
                    />
                    <SortableTh
                      label="Unit Price"
                      sortKey="unit_price"
                      sort={salesSort.sort}
                      dir={salesSort.dir}
                      onToggle={salesSort.toggleSort}
                      align="right"
                    />
                    <SortableTh
                      label="Line Total"
                      sortKey="line_total"
                      sort={salesSort.sort}
                      dir={salesSort.dir}
                      onToggle={salesSort.toggleSort}
                      align="right"
                    />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {salesSort.sorted.map((row) => {
                    if (row.kind === 'return') {
                      return (
                        <tr key={`sret-${row.id}`} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <Link
                              to={`/returns/${row.id}`}
                              className="font-mono text-primary hover:underline"
                            >
                              {row.number}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {formatDate(row.date)}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="red">Return</Badge>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-red-600">
                            {row.qty}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            {formatCents(row.unit_price)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium text-red-600">
                            {formatCents(row.line_total)}
                          </td>
                        </tr>
                      );
                    }
                    const badge = ESTIMATE_STATUS_BADGE[row.status];
                    return (
                      <tr key={`sale-${row.id}`} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <Link
                            to={`/estimates/${row.id}`}
                            className="font-mono text-primary hover:underline"
                          >
                            {row.number}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(row.date)}</td>
                        <td className="px-4 py-3">
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{row.qty}</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {formatCents(row.unit_price)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium">
                          {formatCents(row.line_total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t bg-muted/30">
                    <td colSpan={6} className="px-4 py-3 text-right text-muted-foreground">
                      Net sold{' '}
                      <span className="text-foreground font-medium tabular-nums">
                        {roundQty(salesFooter.netQty)}
                      </span>{' '}
                      · Sales{' '}
                      <span className="text-foreground font-medium tabular-nums">
                        {formatCents(salesFooter.salesTotal)}
                      </span>{' '}
                      · Returns{' '}
                      <span className="text-red-600 font-medium tabular-nums">
                        −{formatCents(salesFooter.returnsTotal)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        )}

        {/* Tab: Purchase History (POs + purchase returns) */}
        {activeTab === 'purchases' && (
          <div className="rounded-md border overflow-hidden">
            {ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !purchaseRows.length ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                No purchase history yet.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <SortableTh
                      label="PO"
                      sortKey="number"
                      sort={purchaseSort.sort}
                      dir={purchaseSort.dir}
                      onToggle={purchaseSort.toggleSort}
                    />
                    <SortableTh
                      label="Date"
                      sortKey="date"
                      sort={purchaseSort.sort}
                      dir={purchaseSort.dir}
                      onToggle={purchaseSort.toggleSort}
                    />
                    <SortableTh
                      label="Status"
                      sortKey="status"
                      sort={purchaseSort.sort}
                      dir={purchaseSort.dir}
                      onToggle={purchaseSort.toggleSort}
                    />
                    <SortableTh
                      label="Qty"
                      sortKey="qty"
                      sort={purchaseSort.sort}
                      dir={purchaseSort.dir}
                      onToggle={purchaseSort.toggleSort}
                      align="right"
                    />
                    <SortableTh
                      label="Unit Price"
                      sortKey="unit_price"
                      sort={purchaseSort.sort}
                      dir={purchaseSort.dir}
                      onToggle={purchaseSort.toggleSort}
                      align="right"
                    />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {purchaseSort.sorted.map((row) => {
                    if (row.kind === 'return') {
                      return (
                        <tr key={`pret-${row.id}`} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <Link
                              to={`/purchase-returns/${row.id}`}
                              className="font-mono text-primary hover:underline"
                            >
                              {row.number}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {formatDate(row.date)}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="red">Return</Badge>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-red-600">
                            {row.qty}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            {formatCents(row.unit_price)}
                          </td>
                        </tr>
                      );
                    }
                    const badge = PO_STATUS_BADGE[row.status];
                    return (
                      <tr key={`po-${row.id}`} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <Link
                            to={`/purchase-orders/${row.id}`}
                            className="font-mono text-primary hover:underline"
                          >
                            {row.number}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(row.date)}</td>
                        <td className="px-4 py-3">
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{row.qty}</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {formatCents(row.unit_price)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t bg-muted/30">
                    <td colSpan={5} className="px-4 py-3 text-right text-muted-foreground">
                      Net purchased{' '}
                      <span className="text-foreground font-medium tabular-nums">
                        {roundQty(purchaseFooter.netQty)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editOpen && (
        <ItemEditModal item={item} onClose={() => setEditOpen(false)} onSaved={refetch} />
      )}
    </div>
  );
}
